import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

import SmartLink from "@/components/SmartLink";
import { Button } from "@/components/ui/button";
import { siteConfig } from "@/content/site";

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  const isActive = (href) => location.pathname === href;

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 glass-nav">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link 
            to="/" 
            className="font-sans font-semibold text-lg md:text-xl text-foreground hover:text-primary transition-colors"
            data-testid="nav-logo"
          >
            {siteConfig.brandName}
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-6">
            {siteConfig.navLinks.map((link) => (
              <SmartLink
                key={link.label}
                href={link.href}
                external={link.external}
                className={`font-body text-sm font-medium transition-colors ${
                  isActive(link.href)
                    ? "text-highlight"
                    : "text-muted-foreground hover:text-foreground"
                }`}
                data-testid={`nav-${link.label.toLowerCase()}`}
              >
                {link.label}
              </SmartLink>
            ))}
            <Button
              asChild
              className="rounded-full bg-primary hover:bg-primary/90 text-white font-medium px-5 btn-hover"
              data-testid="nav-primary-cta"
            >
              <SmartLink href={siteConfig.navPrimaryCta.href} external={siteConfig.navPrimaryCta.external}>
                {siteConfig.navPrimaryCta.label}
              </SmartLink>
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 text-foreground"
            onClick={() => setIsOpen(!isOpen)}
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-background/95 backdrop-blur-xl border-t border-border"
          >
            <div className="px-6 py-4 space-y-3">
              {siteConfig.navLinks.map((link) => (
                <SmartLink
                  key={link.label}
                  href={link.href}
                  external={link.external}
                  className={`block py-2 font-body text-base font-medium ${
                    isActive(link.href)
                      ? "text-highlight"
                      : "text-muted-foreground"
                  }`}
                  onClick={() => setIsOpen(false)}
                  data-testid={`mobile-nav-${link.label.toLowerCase()}`}
                >
                  {link.label}
                </SmartLink>
              ))}
              <div className="pt-2 flex flex-col gap-3">
                <Button
                  asChild
                  className="w-full rounded-full bg-primary hover:bg-primary/90 text-white font-medium"
                  data-testid="mobile-primary-cta"
                >
                  <SmartLink href={siteConfig.navPrimaryCta.href} external={siteConfig.navPrimaryCta.external}>
                    {siteConfig.navPrimaryCta.label}
                  </SmartLink>
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;
